import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import BackButton from '../../../components/common/BackButton';
import { register as registerUser } from '../../../services/authService';
import {
  getUsers,
  updateUser,
  deleteUser,
} from '../../../services/usersService';
import { getApiErrorMessage } from '../../../services/http';

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'designer', label: 'Designer' },
  { value: 'operator', label: 'Operator' },
];

const Users = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('operator');
  const [phone, setPhone] = useState('');
  const [creating, setCreating] = useState(false);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('operator');
  const [editPhone, setEditPhone] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [savingEdit, setSavingEdit] = useState(false);

  const filtered = users.filter(u => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.role || '').toLowerCase().includes(q);
  });

  const exportToCSV = () => {
    try {
      const cols = [
        { header: 'Name', accessor: r => r.name },
        { header: 'Email', accessor: r => r.email },
        { header: 'Role', accessor: r => r.role },
        { header: 'Phone', accessor: r => r.phone },
        { header: 'Active', accessor: r => r.isActive ? 'Yes' : 'No' },
      ];
      const header = cols.map(c => '"' + c.header + '"').join(',');
      const body = filtered.map(r => cols.map(c => '"' + (c.accessor(r) ?? '') + '"').join(',')).join('\n');
      const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      console.error('Export failed', e);
      toast.error('Export failed');
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await getUsers();
      setUsers(data || []);
    } catch (e) {
      console.error('Load users failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to load users (Admin/Manager only)'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password) {
      toast.error('Name, Email, and Password are required');
      return;
    }
    const pw = password;
    if (pw.length < 12) {
      toast.error('Password must be at least 12 characters');
      return;
    }
    if (!/[A-Z]/.test(pw) || !/[a-z]/.test(pw) || !/[0-9]/.test(pw) || !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw)) {
      toast.error('Password must include uppercase, lowercase, number, and special character');
      return;
    }
    try {
      setCreating(true);
      await registerUser({
        name: name.trim(),
        email: email.trim(),
        password,
        role,
        phone: phone.trim() || undefined,
      });
      toast.success('User created');
      setName('');
      setEmail('');
      setPassword('');
      setRole('operator');
      setPhone('');
      loadUsers();
    } catch (e) {
      console.error('Create user failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to create user'));
    } finally {
      setCreating(false);
    }
  };

  const onEdit = (u) => {
    setEditId(u._id);
    setEditName(u.name || '');
    setEditEmail(u.email || '');
    setEditRole(u.role || 'operator');
    setEditPhone(u.phone || '');
    setEditActive(u.isActive !== false);
  };

  const onSaveEdit = async () => {
    if (!editId) return;
    if (!editName.trim() || !editEmail.trim()) {
      toast.error('Name and Email are required');
      return;
    }
    try {
      setSavingEdit(true);
      await updateUser(editId, {
        name: editName.trim(),
        email: editEmail.trim(),
        role: editRole,
        phone: editPhone.trim() || undefined,
        isActive: editActive,
      });
      toast.success('User updated');
      setEditId(null);
      loadUsers();
    } catch (e) {
      console.error('Update user failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to update user'));
    } finally {
      setSavingEdit(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteUser(id);
      toast.success('User deleted');
      loadUsers();
    } catch (e) {
      console.error('Delete user failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to delete user'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <BackButton to="/dashboard" />
        <h2 className="text-2xl font-bold text-gray-900">Users</h2>
      </div>

      <form onSubmit={onCreate} className="card p-6 space-y-4">
        <h3 className="text-lg font-medium text-gray-900">New User</h3>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input className="input-field" value={name} onChange={e => setName(e.target.value)} placeholder="Full name" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" className="input-field" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input type="password" className="input-field" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 12 chars, Aa0!" title="Min 12 chars, uppercase, lowercase, number, special char" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select className="input-field" value={role} onChange={e => setRole(e.target.value)}>
              {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input className="input-field" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+2547XXXXXXX" />
          </div>
        </div>
        <button type="submit" className="btn-primary" disabled={creating}>{creating ? 'Saving...' : 'Create User'}</button>
      </form>

      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 max-w-md">
            <input className="input-field" placeholder="Search users" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn-secondary ml-4" onClick={exportToCSV}>Export CSV</button>
        </div>
      </div>

      <div className="card p-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map(u => (
                <tr key={u._id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {editId === u._id ? (
                      <input className="input-field" value={editName} onChange={e => setEditName(e.target.value)} />
                    ) : (
                      u.name
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {editId === u._id ? (
                      <input type="email" className="input-field" value={editEmail} onChange={e => setEditEmail(e.target.value)} />
                    ) : (
                      u.email
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {editId === u._id ? (
                      <select className="input-field" value={editRole} onChange={e => setEditRole(e.target.value)}>
                        {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    ) : (
                      u.role
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {editId === u._id ? (
                      <input className="input-field" value={editPhone} onChange={e => setEditPhone(e.target.value)} />
                    ) : (
                      u.phone || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {editId === u._id ? (
                      <label className="inline-flex items-center text-sm text-gray-700">
                        <input type="checkbox" className="mr-2" checked={editActive} onChange={e => setEditActive(e.target.checked)} /> Active
                      </label>
                    ) : (
                      u.isActive ? 'Yes' : 'No'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    {editId === u._id ? (
                      <div className="flex justify-end gap-2">
                        <button className="btn-primary" onClick={onSaveEdit} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save'}</button>
                        <button className="btn-secondary" onClick={() => setEditId(null)}>Cancel</button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <button className="btn-secondary" onClick={() => onEdit(u)}>Edit</button>
                        <button className="btn-danger" onClick={() => onDelete(u._id)}>Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <div className="text-center text-gray-500 py-6">Loading...</div>}
          {!loading && filtered.length === 0 && <div className="text-center text-gray-500 py-6">No users</div>}
        </div>
      </div>
    </div>
  );
};

export default Users;
